import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import ImageCropModal from '../common/ImageCropModal';
import ListingPhotosGuidance from './ListingPhotosGuidance';
import PhotoSlotGrid from './PhotoSlotGrid';
import {
  SHOP_MEDIA_ACCEPT,
  validateShopMediaFiles,
  countShopImageFiles,
  isShopVideoFile,
} from '../../utils/shopMedia';
import { cancelPendingUpload, isAbortError, uploadPendingImage } from '../../utils/pendingImageUploads';

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldLabel = styled.label`
  font-weight: 600;
  color: #1c1917;
  font-size: 0.9rem;
`;

const OptionalHint = styled.span`
  font-weight: 500;
  color: #78716c;
  font-size: 0.82rem;
`;

const NeedMore = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #b45309;
  font-weight: 500;
`;

const UploadStatus = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #047857;
  font-weight: 700;
`;

const HiddenInput = styled.input`
  display: none;
`;

/**
 * Shared media picker for designer/boutique/artist listing forms.
 *
 * When `pickTimeUpload` is true, each file uploads to R2 as soon as it is
 * picked (or cropped), with a percentage overlay — matching individual
 * CreateItem listings. At submit, call `uploadApiRef.current.waitForUploads()`.
 */
const ListingMediaUploadField = ({
  mediaItems = [],
  onChange,
  maxMedia = 10,
  minMedia = 1,
  minPhotos = 1,
  minRecommendedPhotos = 3,
  idPrefix = 'listing-media',
  label = 'Photos & videos',
  showGuidance = true,
  pickTimeUpload = false,
  uploadApiRef = null,
}) => {
  const inputRef = useRef(null);
  const mediaIdRef = useRef(0);
  const uploadControllersRef = useRef(new Map());
  const uploadPromisesRef = useRef(new Map());
  const mediaItemsRef = useRef(mediaItems);
  const [cropModal, setCropModal] = useState(null);
  const [cropQueue, setCropQueue] = useState([]);
  const cropModalRef = useRef(cropModal);
  const cropQueueRef = useRef(cropQueue);
  const photoCount = countShopImageFiles(mediaItems);
  const remaining = Math.max(0, minMedia - mediaItems.length);
  const uploadingItems = mediaItems.filter((item) => item.upload?.status === 'uploading');
  const uploadingCount = uploadingItems.length;
  const uploadPercent = uploadingCount
    ? Math.round(
      uploadingItems.reduce((sum, item) => sum + (Number(item.upload?.progress) || 0), 0) / uploadingCount,
    )
    : 0;

  useEffect(() => {
    mediaItemsRef.current = mediaItems;
  }, [mediaItems]);

  useEffect(() => {
    cropModalRef.current = cropModal;
  }, [cropModal]);

  useEffect(() => {
    cropQueueRef.current = cropQueue;
  }, [cropQueue]);

  // Keep a synchronous copy so pick-time uploads can patch progress without
  // racing the parent render that follows onChange.
  const setMedia = (next) => {
    mediaItemsRef.current = next;
    onChange(next);
  };

  const updateItemUpload = (id, patch) => {
    setMedia(
      mediaItemsRef.current.map((item) =>
        item.id === id ? { ...item, upload: { ...item.upload, ...patch } } : item,
      ),
    );
  };

  const startMediaUpload = (item, { fresh = false } = {}) => {
    if (!pickTimeUpload || !item?.file || !item?.id) {
      return Promise.resolve({ skipped: true });
    }
    uploadControllersRef.current.get(item.id)?.abort();
    const begin = async () => {
      if (fresh) {
        await cancelPendingUpload(item.id);
      }
      const controller = new AbortController();
      uploadControllersRef.current.set(item.id, controller);
      updateItemUpload(item.id, {
        status: 'uploading',
        progress: 0,
        key: undefined,
        error: undefined,
      });
      try {
        const result = await uploadPendingImage({
          file: item.file,
          imageId: item.id,
          signal: controller.signal,
          allowVideo: true,
          onProgress: (_label, meta) => {
            if (typeof meta?.percent === 'number') {
              updateItemUpload(item.id, { status: 'uploading', progress: meta.percent });
            }
          },
        });
        uploadControllersRef.current.delete(item.id);
        if (result?.deferred) {
          updateItemUpload(item.id, { status: 'deferred', progress: 100 });
          return { deferred: true };
        }
        updateItemUpload(item.id, { status: 'done', progress: 100, key: result.key });
        return { key: result.key };
      } catch (err) {
        uploadControllersRef.current.delete(item.id);
        if (isAbortError(err)) return { aborted: true };
        const msg = err?.response?.data?.error || err?.message || 'upload failed';
        updateItemUpload(item.id, { status: 'error', error: String(msg) });
        return { error: String(msg) };
      }
    };
    const run = begin();
    uploadPromisesRef.current.set(item.id, run);
    return run;
  };

  const retryMediaUpload = (index) => {
    const item = mediaItemsRef.current[index];
    if (item) void startMediaUpload(item);
  };

  const skipRemainingCrops = () => {
    const items = mediaItemsRef.current;
    const current = cropModalRef.current;
    const indexes = [
      ...(current ? [current.index] : []),
      ...cropQueueRef.current,
    ];
    const seen = new Set();
    indexes.forEach((index) => {
      if (seen.has(index)) return;
      seen.add(index);
      const item = items[index];
      if (!item || item.isVideo || isShopVideoFile(item.file)) return;
      if (!item.upload || item.upload.status === 'pending') {
        void startMediaUpload(item);
      }
    });
    setCropModal(null);
    setCropQueue([]);
  };

  const waitForUploads = async () => {
    skipRemainingCrops();
    const items = mediaItemsRef.current;
    items
      .filter((img) => !img.upload || img.upload.status === 'pending' || img.upload.status === 'error')
      .forEach((img) => { void startMediaUpload(img); });
    return Promise.all(
      items.map(async (img) => {
        const fromPromise = await (
          uploadPromisesRef.current.get(img.id) || Promise.resolve(null)
        );
        const latest = mediaItemsRef.current.find((row) => row.id === img.id) || img;
        if (latest.upload?.status === 'done' && latest.upload?.key) {
          return { key: latest.upload.key };
        }
        if (latest.upload?.status === 'deferred') {
          return { deferred: true };
        }
        if (fromPromise?.key || fromPromise?.deferred) {
          return fromPromise;
        }
        if (latest.upload?.status === 'error') {
          return { error: latest.upload.error || fromPromise?.error || 'upload failed' };
        }
        return fromPromise || {};
      }),
    );
  };

  useEffect(() => {
    if (!uploadApiRef) return undefined;
    uploadApiRef.current = {
      waitForUploads,
      startMediaUpload,
      retryMediaUpload,
    };
    return () => {
      if (uploadApiRef.current) uploadApiRef.current = null;
    };
  });

  const openNextInQueue = (queue, items) => {
    if (!queue.length) {
      setCropModal(null);
      setCropQueue([]);
      return;
    }
    const [index, ...rest] = queue;
    const item = items[index];
    if (!item?.preview || item.isVideo || isShopVideoFile(item.file)) {
      openNextInQueue(rest, items);
      return;
    }
    setCropQueue(rest);
    setCropModal({
      index,
      src: item.preview,
      fileName: item.file?.name || `photo_${index + 1}.jpg`,
    });
  };

  const pushFiles = (files) => {
    const room = maxMedia - mediaItems.length;
    if (room <= 0) {
      toast.error(`Maximum ${maxMedia} photos/videos.`);
      return;
    }
    const capped = files.slice(0, room);
    if (files.length > room) {
      toast.error(`Only ${room} more file${room === 1 ? '' : 's'} can be added (max ${maxMedia}).`);
    }
    const { valid, errors } = validateShopMediaFiles(capped);
    errors.forEach((msg) => toast.error(msg));
    if (valid.length === 0) return;

    const startIndex = mediaItems.length;
    const newItems = valid.map((file) => ({
      id: `${Date.now()}_${++mediaIdRef.current}`,
      file,
      preview: URL.createObjectURL(file),
      isVideo: isShopVideoFile(file),
      ...(pickTimeUpload ? { upload: { status: 'pending', progress: 0 } } : {}),
    }));
    const next = [...mediaItems, ...newItems];
    setMedia(next);

    // Videos skip the crop editor — start their uploads immediately.
    if (pickTimeUpload) {
      newItems.filter((item) => item.isVideo).forEach((item) => {
        void startMediaUpload(item);
      });
    }

    const queue = newItems
      .map((item, i) => ({ item, index: startIndex + i }))
      .filter(({ item }) => !item.isVideo)
      .map(({ index }) => index);
    openNextInQueue(queue, next);
  };

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    pushFiles(files);
  };

  const removeAt = (index) => {
    const next = [...mediaItems];
    const [removed] = next.splice(index, 1);
    if (removed?.id) {
      uploadControllersRef.current.get(removed.id)?.abort();
      uploadControllersRef.current.delete(removed.id);
      uploadPromisesRef.current.delete(removed.id);
      if (pickTimeUpload) void cancelPendingUpload(removed.id);
    }
    if (removed?.preview) URL.revokeObjectURL(removed.preview);
    setMedia(next);
  };

  const openCropForIndex = (index) => {
    const item = mediaItems[index];
    if (!item?.preview || item.isVideo || isShopVideoFile(item.file)) return;
    setCropQueue([]);
    setCropModal({
      index,
      src: item.preview,
      fileName: item.file?.name || `photo_${index + 1}.jpg`,
    });
  };

  const applyCropResult = ({ file, preview }) => {
    if (!cropModal) return;
    const { index } = cropModal;
    const next = [...mediaItems];
    const old = next[index];
    if (old?.preview) URL.revokeObjectURL(old.preview);
    const cropped = {
      id: old?.id || `${Date.now()}_${++mediaIdRef.current}`,
      file,
      preview,
      isVideo: false,
      ...(pickTimeUpload ? { upload: { status: 'pending', progress: 0 } } : {}),
    };
    next[index] = cropped;
    setMedia(next);
    if (pickTimeUpload) {
      void startMediaUpload(cropped, { fresh: true });
    }
    openNextInQueue(cropQueue, next);
  };

  const skipCrop = () => {
    if (pickTimeUpload && cropModal) {
      const item = mediaItems[cropModal.index];
      if (item && (!item.upload || item.upload.status === 'pending')) {
        void startMediaUpload(item);
      }
    }
    openNextInQueue(cropQueue, mediaItems);
  };

  return (
    <FieldWrap>
      {showGuidance ? (
        <ListingPhotosGuidance
          photoCount={photoCount}
          supportsVideo
          minRecommended={minRecommendedPhotos}
          maxMedia={maxMedia}
        />
      ) : null}

      <FieldLabel>
        {label} *{' '}
        <OptionalHint>
          (at least {minPhotos} photo{minPhotos === 1 ? '' : 's'}
          {minMedia > minPhotos ? `, ${minMedia}+ media` : ''}
          , max {maxMedia})
        </OptionalHint>
      </FieldLabel>

      {mediaItems.length > 0 && remaining > 0 && (
        <NeedMore>
          Add at least {remaining} more file{remaining > 1 ? 's' : ''} for better visibility
        </NeedMore>
      )}
      {mediaItems.length > 0 && photoCount < minPhotos && (
        <NeedMore>
          Include at least {minPhotos} photo{minPhotos === 1 ? '' : 's'} so the item can show a thumbnail.
        </NeedMore>
      )}
      {uploadingCount > 0 ? (
        <UploadStatus>
          Uploading {uploadingCount} of {mediaItems.length} · {uploadPercent}%
        </UploadStatus>
      ) : null}

      <PhotoSlotGrid
        items={mediaItems}
        maxSlots={maxMedia}
        supportsVideo
        onPickSlot={() => inputRef.current?.click()}
        onRemove={removeAt}
        onEdit={openCropForIndex}
        onRetry={pickTimeUpload ? retryMediaUpload : undefined}
      />

      <HiddenInput
        ref={inputRef}
        id={`${idPrefix}-media`}
        type="file"
        accept={SHOP_MEDIA_ACCEPT}
        multiple
        onChange={handleMediaChange}
      />

      {cropModal ? (
        <ImageCropModal
          imageSrc={cropModal.src}
          fileName={cropModal.fileName}
          remainingAfter={cropQueue.length}
          onCancel={skipCrop}
          onComplete={applyCropResult}
        />
      ) : null}
    </FieldWrap>
  );
};

export default ListingMediaUploadField;
